import { useEffect, useRef, useState } from 'react';
import {
  userErrorMessage,
  ebookDirectoryItemSchema,
  type EbookDocument,
  type EbookDocumentIndex,
  type EbookQuestion,
} from '@wx-weiqi/contracts';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import type { EbookClient } from '../services/ebooks';
import { normalizeEbookChapter } from '../services/ebook-chapters';
import { nextRequestId } from '../services/http';
import { EbookQuestionWriter } from '../services/ebook-question-writer';
import { isEbookQuestionReady } from '../services/ebook-editing';
import {
  newEbookChapterProblem,
  ebookChapterRenameProblem,
} from '../services/ebook-chapter-editing';
import { EbookEditor } from './EbookEditor';
import { EbookThumbnail } from './EbookReader';
import { EbookUnsavedDialog } from './EbookUnsavedDialog';
import { ebookSteps } from './EbookWorkflowNav';
import './ebook-paged-authoring.css';

type Directory = Awaited<ReturnType<EbookClient['directory']>>;
export function EbookPagedAuthoring({
  client,
  documentId,
  onBack,
  onLegacy,
}: {
  readonly client: EbookClient;
  readonly documentId: string;
  readonly onBack: () => void;
  readonly onLegacy: (step: string, questionId?: string) => Promise<void>;
}) {
  const [index, setIndex] = useState<EbookDocumentIndex | null>(null);
  const [directory, setDirectory] = useState<Directory | null>(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState({
    search: '',
    chapter: undefined as string | undefined,
    scope: 'ALL' as 'ALL' | 'ARRANGED',
  });
  const [page, setPage] = useState(1);
  const [jumpPage, setJumpPage] = useState('1');
  const [reload, setReload] = useState(0);
  const [draft, setDraft] = useState<EbookQuestion | null>(null);
  const [saved, setSaved] = useState<EbookQuestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [blocked, setBlocked] = useState(false);
  const [navigation, setNavigation] = useState<(() => void) | null>(null);
  const writer = useRef(new EbookQuestionWriter());
  const inFlight = useRef(false);
  const generation = useRef(0);
  const catalog = useRef<HTMLElement>(null);
  const arrangementRetry = useRef<{ payload: string; requestId: string } | null>(null);
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved);
  const busy = loading || saving || editorBusy;

  useEffect(() => {
    const token = ++generation.current;
    setLoading(true);
    setError('');
    void (async () => {
      const header = await client.documentIndex(documentId);
      const list = await client.directory(documentId, {
        ...query,
        page,
        pageSize: 24,
        expectedVersion: header.stateVersion,
      });
      if (token !== generation.current) return;
      setIndex(header);
      setDirectory(list);
      setBlocked(false);
    })()
      .catch((reason) => {
        if (token === generation.current)
          setError(userErrorMessage(reason, '目录载入失败，请重试。'));
      })
      .finally(() => {
        if (token === generation.current) setLoading(false);
      });
    return () => {
      generation.current++;
    };
  }, [client, documentId, query, page, reload]);
  useEffect(() => {
    const stop = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    if (dirty) window.addEventListener('beforeunload', stop);
    return () => window.removeEventListener('beforeunload', stop);
  }, [dirty]);

  function turnPage(next: number) {
    setPage(next);
    catalog.current?.scrollIntoView({ block: 'start' });
  }
  function navigate(action: () => void) {
    if (busy) return;
    if (dirty) setNavigation(() => action);
    else action();
  }
  async function open(id: string) {
    if (!index || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const result = await client.questionDetail(documentId, id, index.stateVersion);
      setSaved(result.question);
      setDraft(result.question);
      setBlocked(false);
    } catch (reason) {
      setError(userErrorMessage(reason, '题目载入失败，请刷新目录。'));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }
  async function persist(question: EbookQuestion, newChapter?: string) {
    if (!index || inFlight.current || blocked)
      throw new Error('请先刷新教材后再保存，当前修改仍保留。');
    inFlight.current = true;
    setSaving(true);
    setError('');
    try {
      const result = await writer.current.save(
        client,
        documentId,
        index.stateVersion,
        question,
        newChapter,
      );
      setSaved(result.question);
      setDraft(result.question);
      setIndex((current) =>
        current
          ? {
              ...current,
              stateVersion: result.stateVersion,
              book: newChapter
                ? { ...current.book, chapters: [...(current.book.chapters ?? []), newChapter] }
                : current.book,
              chapters: [
                ...new Set([
                  ...current.chapters.map((c) => c.name),
                  normalizeEbookChapter(result.question.chapter),
                ]),
              ].map((name) => ({
                name,
                count:
                  (current.chapters.find((c) => c.name === name)?.count ?? 0) -
                  (name === normalizeEbookChapter(saved?.chapter ?? '') ? 1 : 0) +
                  (name === normalizeEbookChapter(result.question.chapter) ? 1 : 0),
              })),
            }
          : current,
      );
      setDirectory((current) =>
        current
          ? {
              ...current,
              stateVersion: result.stateVersion,
              items: current.items.map((item) =>
                item.id === result.question.id
                  ? ebookDirectoryItemSchema.parse({
                      ...result.question,
                      sourcePage: result.question.source?.pageIndex ?? null,
                      arranged: item.arranged,
                    })
                  : item,
              ),
            }
          : current,
      );
      setNotice('题目已保存。已发布教材需更新电子书后学生才可见。');
      return result;
    } catch (reason) {
      setError(userErrorMessage(reason, '保存失败，修改仍保留，请重试。'));
      throw reason;
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }
  async function arrange(questionId: string, included: boolean) {
    if (!index || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setError('');
    setNotice('');
    const payload = JSON.stringify([index.stateVersion, questionId, included]);
    if (arrangementRetry.current?.payload !== payload)
      arrangementRetry.current = { payload, requestId: nextRequestId('ebook-arrange') };
    try {
      const result = await client.saveArrangement(
        documentId,
        index.stateVersion,
        questionId,
        included,
        arrangementRetry.current.requestId,
      );
      arrangementRetry.current = null;
      setIndex((current) =>
        current
          ? {
              ...current,
              stateVersion: result.stateVersion,
              book: { ...current.book, questionIds: result.questionIds },
            }
          : current,
      );
      setNotice(included ? '已加入编排。更新电子书后学生可见。' : '已移出编排，题目草稿仍保留。');
      setReload((n) => n + 1);
    } catch (reason) {
      setError(userErrorMessage(reason, '编排未确认，请重试；若教材已更新，请先刷新。'));
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }
  async function legacy(step: string) {
    setLoading(true);
    setError('');
    try {
      await onLegacy(step, draft?.id);
    } catch (reason) {
      setError(userErrorMessage(reason, '工作台载入失败'));
      setLoading(false);
    }
  }
  async function refreshEditor() {
    if (!draft) {
      setReload((n) => n + 1);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const header = await client.documentIndex(documentId);
      const latest = await client.questionDetail(documentId, draft.id, header.stateVersion);
      setIndex(header);
      // Never silently overwrite unsaved work after a conflict.
      if (dirty && latest.question.revision !== saved?.revision) {
        setBlocked(true);
        setNotice('服务器上的这道题已变化。请先复制需要保留的内容，再选择“重新载入题目”。');
      } else {
        setSaved(latest.question);
        if (!dirty) setDraft(latest.question);
        setBlocked(false);
      }
    } catch (reason) {
      setError(userErrorMessage(reason, '刷新失败'));
    } finally {
      setLoading(false);
    }
  }
  const names = [
    ...new Set([
      ...(index?.book.chapters ?? []),
      ...(index?.chapters.map((c) => c.name).filter(Boolean) ?? []),
    ]),
  ];
  const editorDocument: EbookDocument | null =
    index && saved
      ? {
          id: index.id,
          name: index.name,
          hash: index.hash,
          pageCount: index.pageCount,
          stateVersion: index.stateVersion,
          cancelled: index.cancelled,
          updatedAt: index.updatedAt,
          pages: index.pages,
          data: { schemaVersion: 1, book: index.book, questions: [saved], relations: [] },
        }
      : null;
  return (
    <main className="ebook-page ebook-teacher is-document ebook-paged-authoring" aria-busy={busy}>
      <header className="ebook-header">
        <div>
          <h1>{index?.book.title ?? '我的教材'}</h1>
          {index && (
            <p>
              {index.pageCount} 页 PDF · {index.draftCount} 条草稿 · {index.book.questionIds.length}{' '}
              题已编排
            </p>
          )}
        </div>
        <button disabled={busy} onClick={() => navigate(onBack)}>
          <ArrowLeftIcon aria-hidden="true" />
          返回教材
        </button>
      </header>
      <nav className="ebook-workflow-strip" aria-label="电子书工作台">
        {ebookSteps.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            disabled={busy}
            aria-pressed={draft ? id === 'edit' : id === 'library'}
            onClick={() =>
              navigate(() => {
                if (id === 'library' || id === 'edit') {
                  setDraft(null);
                  setSaved(null);
                  setReload((n) => n + 1);
                } else void legacy(id);
              })
            }
          >
            <Icon aria-hidden="true" />
            <span>
              <strong>{label}</strong>
            </span>
          </button>
        ))}
      </nav>
      {error && (
        <p role="alert" className="ebook-error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="ebook-notice">
          {notice}
        </p>
      )}
      {(error || blocked) && (
        <div className="ebook-actions">
          <button disabled={busy} onClick={() => void refreshEditor()}>
            刷新教材
          </button>
          {blocked && draft && (
            <button disabled={busy} onClick={() => navigate(() => void open(draft.id))}>
              重新载入题目
            </button>
          )}
        </div>
      )}
      {navigation && (
        <EbookUnsavedDialog
          busy={busy}
          error={error}
          onStay={() => setNavigation(null)}
          onDiscard={() => {
            setDraft(saved);
            const next = navigation;
            setNavigation(null);
            next();
          }}
          onSave={() => {
            if (draft)
              void persist(draft)
                .then(() => {
                  const next = navigation;
                  setNavigation(null);
                  next();
                })
                .catch(() => {});
          }}
        />
      )}
      {draft && editorDocument && index ? (
        <>
          <div className="ebook-row">
            <h2>题目校对 · {draft.number || draft.title}</h2>
            <button
              disabled={busy}
              onClick={() =>
                navigate(() => {
                  setDraft(null);
                  setSaved(null);
                  setReload((n) => n + 1);
                })
              }
            >
              返回题目目录
            </button>
          </div>
          <EbookEditor
            key={draft.id}
            client={client}
            document={editorDocument}
            question={draft}
            chapters={names}
            chapterQuestionCount={
              index.chapters.find((c) => c.name === normalizeEbookChapter(draft.chapter))?.count ??
              0
            }
            busy={loading || saving || blocked}
            dirty={dirty}
            onBusyChange={setEditorBusy}
            onChange={setDraft}
            onPersist={async (question) => {
              await persist(question);
            }}
            onComplete={async (question) => {
              const result = await persist(question);
              if (!isEbookQuestionReady(result.question))
                throw new Error('已保存，请确认棋形与答案后完成入库。');
            }}
            onCreateChapter={async (name, question) => {
              const problem = newEbookChapterProblem(name, names);
              if (problem) throw new Error(problem);
              await persist({ ...question, chapter: name.trim() }, name.trim());
            }}
            onRenameChapter={async (currentName, nextName) => {
              const problem = ebookChapterRenameProblem(nextName, currentName, names);
              if (problem) throw new Error(problem);
              const version = dirty ? (await persist(draft)).stateVersion : index.stateVersion;
              try {
                await client.mutate(documentId, {
                  action: 'RENAME_CHAPTER',
                  expectedVersion: version,
                  currentName,
                  nextName,
                });
              } catch (reason) {
                setBlocked(true);
                setError(userErrorMessage(reason, '章节保存结果尚未确认，请刷新教材核对。'));
                throw reason;
              }
              setBlocked(true);
              try {
                const header = await client.documentIndex(documentId);
                const result = await client.questionDetail(
                  documentId,
                  draft.id,
                  header.stateVersion,
                );
                setIndex(header);
                setSaved(result.question);
                setDraft(result.question);
                setBlocked(false);
              } catch {
                setError('章节已重命名，暂未取得最新内容。请刷新教材，无需重复提交。');
              }
            }}
            onRegion={(region) => {
              void (async () => {
                const version = dirty ? (await persist(draft)).stateVersion : index.stateVersion;
                await client.mutate(documentId, {
                  action: 'RECOGNIZE',
                  expectedVersion: version,
                  pageIndex: draft.source?.pageIndex ?? 0,
                  region,
                });
                await legacy('pages');
              })().catch((reason) => setError(userErrorMessage(reason, '补框识别失败')));
            }}
          />
        </>
      ) : (
        <section ref={catalog} aria-label="教材题库" className="ebook-card">
          <form
            className="ebook-paged-toolbar"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setQuery((current) => ({ ...current, search }));
            }}
          >
            <label>
              查找题目
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="题号、名称或要求"
              />
            </label>
            <label>
              章节
              <select
                aria-label="章节"
                value={query.chapter ?? '__all__'}
                onChange={(event) => {
                  setPage(1);
                  setQuery((current) => ({
                    ...current,
                    chapter: event.target.value === '__all__' ? undefined : event.target.value,
                  }));
                }}
              >
                <option value="__all__">全部章节</option>
                <option value="">未分章</option>
                {names.map((name) => (
                  <option key={name}>{name}</option>
                ))}
              </select>
            </label>
            <label>
              范围
              <select
                aria-label="范围"
                value={query.scope}
                onChange={(event) => {
                  setPage(1);
                  setQuery((current) => ({
                    ...current,
                    scope: event.target.value as 'ALL' | 'ARRANGED',
                  }));
                }}
              >
                <option value="ALL">全部草稿</option>
                <option value="ARRANGED">已编排题目</option>
              </select>
            </label>
            <button type="submit" disabled={busy}>
              搜索
            </button>
            <button type="button" disabled={busy} onClick={() => void legacy('library')}>
              批量管理
            </button>
          </form>
          <p className="ebook-muted" role="status">
            {loading ? '正在读取目录…' : `共 ${directory?.total ?? 0} 道题`}
          </p>
          <div className="ebook-paged-grid">
            {directory?.items.map((item) => (
              <article key={item.id} className="ebook-paged-item">
                <button
                  disabled={busy}
                  className="ebook-paged-open"
                  onClick={() => void open(item.id)}
                  aria-label={`编辑${item.number ? `第 ${item.number} 题` : item.title}`}
                >
                  <EbookThumbnail question={item} />
                  <strong>{item.number ? `第 ${item.number} 题` : item.title}</strong>
                  <span>
                    {item.chapter || '未分章'} · {item.arranged ? '已加入编排' : '未加入编排'}
                  </span>
                </button>
                <button disabled={busy} onClick={() => void arrange(item.id, !item.arranged)}>
                  {item.arranged ? '移出编排' : '加入编排'}
                </button>
              </article>
            ))}
          </div>
          {!loading && directory?.total === 0 && (
            <p>没有符合条件的题目。可调整筛选，或进入页面识别添加题目。</p>
          )}
          <nav className="ebook-paged-pagination" aria-label="题目目录分页">
            <button
              disabled={busy || !directory || directory.page <= 1}
              onClick={() => turnPage((directory?.page ?? 1) - 1)}
            >
              <ChevronLeftIcon aria-hidden="true" />
              上一页
            </button>
            <span>
              第 {directory?.page ?? 1} / {Math.max(1, Math.ceil((directory?.total ?? 0) / 24))} 页
            </span>
            <button
              disabled={busy || !directory || directory.page * 24 >= directory.total}
              onClick={() => turnPage((directory?.page ?? 1) + 1)}
            >
              下一页
              <ChevronRightIcon aria-hidden="true" />
            </button>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                const value = Number(jumpPage);
                if (Number.isInteger(value) && value >= 1 && directory)
                  turnPage(Math.min(value, Math.max(1, Math.ceil(directory.total / 24))));
              }}
            >
              <label>
                页码
                <input
                  aria-label="跳转页码"
                  type="number"
                  min={1}
                  max={Math.max(1, Math.ceil((directory?.total ?? 0) / 24))}
                  value={jumpPage}
                  onChange={(event) => setJumpPage(event.target.value)}
                />
              </label>
              <button disabled={busy} type="submit">
                跳转
              </button>
            </form>
          </nav>
        </section>
      )}
    </main>
  );
}
